// src/hr/portal/ProfileTimeOff.js -- the profile's "Time off" tab (mockup screen 3): the allowance
// editor, the entries HR can edit or remove (with documentation toggles on 3+ day sick runs, spec S7),
// protected leave with its running totals (spec M6), what's coming up and the latest changes.
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  FiAlertCircle, FiAlertTriangle, FiArrowRight, FiCheck, FiEdit2, FiEdit3, FiPlus, FiShield, FiTrash2
} from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Card, EmptyState, Pill, Seg } from '../kit';
import { plainId } from '../kit/util';
import { daysLabel, shortRangeLabel } from '../format';
import { typeLabel, typeTone, lengthWords } from '../timeoff/labels';
import { workdaysBetween } from '../timeoff/dates';
import {
  SICK_MIN_HOURS, SICK_MIN_MESSAGE, actorLabel, describeChange, fromUnit, fullDate, inSickRun, protectedShare,
  protectedTotalText, stampLabel, toUnit, unitNumber
} from './portalUtils';

const UNITS = [{ value: 'days', label: 'Days' }, { value: 'hours', label: 'Hours' }];
const EDIT_FIELDS = [
  { key: 'allowanceHours', label: 'this year' },
  { key: 'carriedOverHours', label: 'carried over' },
  { key: 'usedBeforePortalHours', label: 'used before the portal' }
];
// Used in the portal itself (the server's usedHours includes time used before the portal).
const usedHere = (b) => (Number(b.usedHours) || 0) - (Number(b.usedBeforePortalHours) || 0);

// ---------------------------------------------------------------------------------------------
// Balances + allowance editor
// ---------------------------------------------------------------------------------------------

function BalancesCard({ personId, year, balances, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [unit, setUnit] = useState('days');
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ids = `hrp-al-${plainId(useId())}`;
  const firstInput = useRef(null);
  const editBtn = useRef(null);
  const wasEditing = useRef(false);

  useEffect(() => {
    if (editing && firstInput.current) firstInput.current.focus();
    else if (!editing && wasEditing.current && editBtn.current) editBtn.current.focus();
    wasEditing.current = editing;
  }, [editing]);

  // Each field keeps its exact hours; the text box is only a view of them in the chosen unit. An
  // untouched field always holds the server's own number, so opening the editor or switching units
  // can never change a stored value (days * 8 and hours / 8 are exact in floating point anyway).
  const startEdit = () => {
    setValues(Object.fromEntries(balances.map((b) => [
      b.typeId,
      Object.fromEntries(EDIT_FIELDS.map((f) => {
        const hours = Number(b[f.key]) || 0;
        return [f.key, { text: String(toUnit(hours, unit)), hours }];
      }))
    ])));
    setError('');
    setEditing(true);
  };

  const hoursOf = (typeId, key) => {
    const field = values[typeId] && values[typeId][key];
    return field ? field.hours : NaN;
  };

  const switchUnit = (next) => {
    if (next === unit) return;
    setValues((current) => Object.fromEntries(Object.entries(current).map(([typeId, row]) => [
      typeId,
      Object.fromEntries(Object.entries(row).map(([key, field]) => [
        key,
        Number.isFinite(field.hours) ? { ...field, text: String(toUnit(field.hours, next)) } : field
      ]))
    ])));
    setUnit(next);
  };

  // What HR types becomes the field's hours; anything that isn't a number is kept as typed (and
  // shown as an error) without touching the other fields.
  const setValue = (typeId, key, text) => {
    const n = Number(text);
    const hours = String(text).trim() !== '' && Number.isFinite(n) ? fromUnit(n, unit) : NaN;
    setValues((current) => ({ ...current, [typeId]: { ...current[typeId], [key]: { text, hours } } }));
    if (error) setError('');
  };

  const fieldError = (b, key) => {
    const h = hoursOf(b.typeId, key);
    if (!Number.isFinite(h) || h < 0) return 'Enter 0 or more';
    if (key === 'allowanceHours' && b.isSick && h < SICK_MIN_HOURS) return SICK_MIN_MESSAGE;
    return null;
  };
  const errors = editing
    ? balances.flatMap((b) => EDIT_FIELDS.map((f) => ({ b, f, message: fieldError(b, f.key) }))).filter((x) => x.message)
    : [];
  // Compared in hours against the server's values.
  const changed = editing
    ? balances.filter((b) => EDIT_FIELDS.some((f) => hoursOf(b.typeId, f.key) !== (Number(b[f.key]) || 0)))
    : [];

  const save = async () => {
    if (errors.length || changed.length === 0 || busy) return;
    const rows = changed.map((b) => ({
      typeId: b.typeId,
      allowanceHours: hoursOf(b.typeId, 'allowanceHours'),
      carriedOverHours: hoursOf(b.typeId, 'carriedOverHours'),
      usedBeforePortalHours: hoursOf(b.typeId, 'usedBeforePortalHours')
    }));
    setBusy(true);
    setError('');
    try {
      const result = await hr.admin.allowances.put(personId, year, { rows });
      setBusy(false);
      setEditing(false);
      onSaved((result && result.balances) || null);
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "Couldn't save the allowances. Try again."));
    }
  };

  const shown = (hours) => unitNumber(hours, editing ? unit : 'days');
  const unitWord = editing && unit === 'hours' ? 'hours' : 'days';
  const errorId = (b, key) => `${ids}-${b.typeId}-${key}-err`;
  const firstEditable = balances[0] ? balances[0].typeId : null;

  const action = editing ? (
    <div className="hrp-al__units">
      <span className="hrp-al__unitlab" id={`${ids}-units`}>Units</span>
      <Seg size="sm" labelledBy={`${ids}-units`} options={UNITS} value={unit} onChange={switchUnit} />
    </div>
  ) : (
    <button ref={editBtn} type="button" className="hrp-link" onClick={startEdit}>
      <FiEdit2 aria-hidden="true" /> Edit allowances
    </button>
  );

  return (
    <Card delay={1} as="section" aria-label={`${year} balances`} title={`${year} balances`} action={action} className="hrp-al">
      {balances.length === 0 ? (
        <EmptyState compact title="No time-off types yet">Add types in Settings to give people balances.</EmptyState>
      ) : (
        <>
          <div className="hrp-table-wrap">
            <table className="hrp-table hrp-stack-table hrp-al__table">
              <caption className="hrp-sr">{`${year} balances in ${unitWord}`}</caption>
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col">This year</th>
                  <th scope="col">Carried over</th>
                  <th scope="col">Used before portal</th>
                  <th scope="col">Used</th>
                  <th scope="col">Scheduled</th>
                  <th scope="col">Pending</th>
                  <th scope="col">Available</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => {
                  const name = typeLabel(b);
                  const available = editing && EDIT_FIELDS.every((f) => Number.isFinite(hoursOf(b.typeId, f.key)))
                    ? hoursOf(b.typeId, 'allowanceHours') + hoursOf(b.typeId, 'carriedOverHours') - hoursOf(b.typeId, 'usedBeforePortalHours')
                      - usedHere(b) - (Number(b.scheduledHours) || 0) - (Number(b.pendingHours) || 0)
                    : Number(b.availableHours) || 0;
                  return (
                    <tr key={b.typeId}>
                      <th scope="row" data-label="Type" className="hrp-al__type"><Pill {...typeTone(b)} dot>{name}</Pill></th>
                      {EDIT_FIELDS.map((f, i) => {
                        const label = i === 0 ? 'This year' : i === 1 ? 'Carried over' : 'Used before portal';
                        if (!editing) {
                          return (
                            <td key={f.key} data-label={label}>
                              {i === 0 && !b.setUp ? <span className="hrp-dim">Not set up</span> : shown(b[f.key])}
                            </td>
                          );
                        }
                        const message = fieldError(b, f.key);
                        return (
                          <td key={f.key} data-label={label}>
                            <input
                              ref={b.typeId === firstEditable && i === 0 ? firstInput : undefined}
                              type="number"
                              className="hrp-input hrp-al__num"
                              min={0}
                              step={unit === 'hours' ? 1 : 0.5}
                              inputMode="decimal"
                              aria-label={`${name} ${f.label}`}
                              aria-invalid={message ? 'true' : undefined}
                              aria-describedby={message ? errorId(b, f.key) : undefined}
                              value={values[b.typeId] && values[b.typeId][f.key] ? values[b.typeId][f.key].text : ''}
                              onChange={(e) => setValue(b.typeId, f.key, e.target.value)}
                            />
                          </td>
                        );
                      })}
                      <td data-label="Used">{shown(usedHere(b))}</td>
                      <td data-label="Scheduled">{shown(b.scheduledHours)}</td>
                      <td data-label="Pending">{shown(b.pendingHours)}</td>
                      <td data-label="Available" className="hrp-al__left" data-neg={available < 0 ? 'true' : undefined}>{shown(available)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {errors.length > 0 && (
            <ul className="hrp-al__errs">
              {errors.map(({ b, f, message }) => (
                <li key={`${b.typeId}-${f.key}`} id={errorId(b, f.key)} className="hrp-field__err" role="alert">
                  {message === SICK_MIN_MESSAGE ? message : `${typeLabel(b)} ${f.label}: ${message}`}
                </li>
              ))}
            </ul>
          )}
          {editing ? (
            <div className="hrp-al__foot">
              <p className="hrp-al__hint">
                {`In ${unit}. Available = this year + carried over − used before the portal − used − scheduled − pending. Sick & Safe is at least 40 hours (5 days) a year.`}
              </p>
              {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
              <div className="hrp-al__btns">
                <Btn variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={busy}>Cancel</Btn>
                <Btn size="sm" icon={FiCheck} loading={busy} disabled={errors.length > 0 || changed.length === 0} onClick={save}>Save allowances</Btn>
              </div>
            </div>
          ) : (
            <p className="hrp-al__hint">In days. Used includes everything taken up to today; scheduled is booked after today.</p>
          )}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------
// Entries
// ---------------------------------------------------------------------------------------------

function entryLength(entry, holidays) {
  const days = workdaysBetween(entry.startDate, entry.endDate, holidays).length;
  return lengthWords(entry.hoursPerDay, entry.totalHours || days * entry.hoursPerDay);
}

const SOURCE_WORDS = { request: 'From a request', sickReport: 'Reported by them' };

function EntryRow({ entry, today, sickRun, holidays, onEdit, onRemoved, onPatched }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const keepRef = useRef(null);
  const removeRef = useRef(null);
  const wasConfirming = useRef(false);
  const range = shortRangeLabel(entry.startDate, entry.endDate);
  const name = entry.protected ? entry.typeName : typeLabel(entry);
  const scheduled = entry.startDate > today;

  useEffect(() => {
    if (confirming && keepRef.current) keepRef.current.focus();
    else if (!confirming && wasConfirming.current && removeRef.current) removeRef.current.focus();
    wasConfirming.current = confirming;
  }, [confirming]);

  const remove = async () => {
    setBusy('remove');
    setError('');
    try {
      await hr.admin.entries.remove(entry.id);
      onRemoved(entry);
    } catch (err) {
      setBusy(null);
      setError(errorText(err, "Couldn't remove the entry. Try again."));
    }
  };

  const toggle = async (field) => {
    setBusy(field);
    setError('');
    try {
      const result = await hr.admin.entries.update(entry.id, { [field]: !entry[field] });
      onPatched({ ...entry, [field]: !entry[field], ...((result && result.entry) || {}) });
    } catch (err) {
      setError(errorText(err, "Couldn't save that. Try again."));
    } finally {
      setBusy(null);
    }
  };

  let desc = entry.note;
  if (!desc) desc = entry.isSick ? '—' : entryLength(entry, holidays);

  return (
    <li className="hrp-ent" data-testid={`hrp-entry-${entry.id}`}>
      <span className="hrp-ent__dt">{range}</span>
      <span className="hrp-ent__type">
        <Pill {...typeTone(entry)} dot={!entry.protected} icon={entry.protected ? <FiShield /> : undefined}>{name}</Pill>
      </span>
      <span className="hrp-ent__desc">
        <span className={entry.note ? undefined : 'hrp-muted'}>{desc}</span>
        {SOURCE_WORDS[entry.source] && <em>{SOURCE_WORDS[entry.source]}</em>}
      </span>
      <span className="hrp-ent__len">
        {scheduled ? <Pill tone="ok">Scheduled</Pill> : null}
        <span>{daysLabel(entry.totalHours)}</span>
      </span>
      <span className="hrp-ent__acts">
        <button type="button" className="hrp-iconbtn hrp-ent__btn" aria-label={`Edit ${name} ${range}`} onClick={() => onEdit(entry)} disabled={!!busy}>
          <FiEdit3 aria-hidden="true" />
        </button>
        <button ref={removeRef} type="button" className="hrp-iconbtn hrp-ent__btn hrp-ent__btn--del" aria-label={`Remove ${name} ${range}`} onClick={() => setConfirming(true)} disabled={!!busy}>
          <FiTrash2 aria-hidden="true" />
        </button>
      </span>

      {sickRun && (
        <div className="hrp-ent__doc" role="group" aria-label={`Documentation for ${range}`}>
          <Pill tone="bad" icon={<FiAlertTriangle />}>3+ sick days in a row</Pill>
          {[['docRequested', 'Documentation requested'], ['docReceived', 'Documentation received']].map(([field, label]) => (
            <button
              key={field}
              type="button"
              className="hrp-toggle"
              aria-pressed={!!entry[field]}
              disabled={busy === field}
              onClick={() => toggle(field)}
            >
              <span className="hrp-toggle__box" aria-hidden="true">{entry[field] ? <FiCheck /> : null}</span>
              {label}
            </button>
          ))}
          <span className="hrp-ent__docnote">The document itself is never stored here.</span>
        </div>
      )}

      {confirming && (
        <div className="hrp-tl__confirm hrp-ent__confirm" role="group" aria-label={`Remove ${name} ${range}?`}>
          <span>
            {entry.protected ? 'Remove this protected leave entry?' : `Remove this entry? Its ${daysLabel(entry.totalHours)} go back to the balance.`}
            {entry.requestId && ' This also cancels the approved request.'}
          </span>
          <span className="hrp-tl__btns">
            <Btn ref={keepRef} variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={busy === 'remove'}>Keep</Btn>
            <Btn variant="danger" size="sm" icon={FiTrash2} loading={busy === 'remove'} onClick={remove}>Remove entry</Btn>
          </span>
        </div>
      )}
      {error && <p className="hrp-tl__err hrp-ent__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
    </li>
  );
}

function EntriesCard({ entries, sickRuns, today, year, holidays, onLog, onEdit, onRemoved, onPatched }) {
  const sorted = [...entries].sort((a, b) => b.startDate.localeCompare(a.startDate));
  return (
    <Card
      delay={2}
      as="section"
      aria-label="Entries"
      title="Entries"
      className="hrp-ents"
      action={<button type="button" className="hrp-link" onClick={onLog}><FiPlus aria-hidden="true" /> Log time off</button>}
    >
      {sorted.length === 0 ? (
        <EmptyState compact title={`Nothing logged in ${year} yet`}>Logged days and approved requests show up here.</EmptyState>
      ) : (
        <ul className="hrp-ents__list">
          {sorted.map((e) => (
            <EntryRow
              key={e.id}
              entry={e}
              today={today}
              holidays={holidays}
              sickRun={inSickRun(e, sickRuns)}
              onEdit={onEdit}
              onRemoved={onRemoved}
              onPatched={onPatched}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ProtectedCard({ entries, totals, today, holidays, onEdit, onRemoved, onPatched }) {
  const sorted = [...entries].sort((a, b) => b.startDate.localeCompare(a.startDate));
  return (
    <Card delay={3} as="section" aria-label="Protected leave" title="Protected leave" className="hrp-prot">
      <p className="hrp-prot__sub"><FiShield aria-hidden="true" />Never deducted from a balance. Only HR sees the type; Who's Out just says “Out”.</p>
      {totals.length > 0 && (
        <ul className="hrp-prot__totals">
          {totals.map((t) => {
            const share = protectedShare(t);
            return (
              <li key={t.typeId}>
                <span className="hrp-prot__name">{t.name}</span>
                <span className="hrp-prot__val">{protectedTotalText(t)}</span>
                {share !== null && (
                  <span className="hrp-prot__track" aria-hidden="true"><span style={{ width: `${Math.round(share * 100)}%` }} /></span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {sorted.length > 0 ? (
        <ul className="hrp-ents__list hrp-prot__list">
          {sorted.map((e) => (
            <EntryRow key={e.id} entry={e} today={today} holidays={holidays} sickRun={false} onEdit={onEdit} onRemoved={onRemoved} onPatched={onPatched} />
          ))}
        </ul>
      ) : (
        <p className="hrp-prot__none">No protected leave logged this year.</p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------
// Right column: sick runs, coming up, change log
// ---------------------------------------------------------------------------------------------

function ComingUp({ reminders, policies, onTab }) {
  const open = (reminders || []).filter((r) => !r.done && r.dueDate).slice(0, 3);
  const unsigned = (policies || []).filter((p) => !p.ackAt).slice(0, 2);
  return (
    <Card delay={2} as="section" aria-label="Coming up" title="Coming up" className="hrp-soon">
      {open.length === 0 && unsigned.length === 0 && <p className="hrp-soon__none">Nothing coming up for them.</p>}
      <ul className="hrp-soon__list">
        {open.map((r) => {
          const over = r.daysLeft !== null && r.daysLeft < 0;
          const alerts = r.kind === 'workAuth' ? '120, 90, 60, 30 and 7' : '60, 30 and 7';
          return (
            <li key={r.id} className="hrp-soon__it" data-tone={over ? 'bad' : 'warn'}>
              <div className="hrp-soon__dd" aria-hidden="true">
                <b>{over ? Math.abs(r.daysLeft) : r.daysLeft}</b>
                <span>{over ? 'days late' : 'days'}</span>
              </div>
              <div>
                <div className="hrp-soon__tt">{r.label}</div>
                <div className="hrp-soon__ts">
                  {over ? `Was due ${fullDate(r.dueDate)}` : `${fullDate(r.dueDate)} · HR gets emails at ${alerts} days`}
                </div>
                <span className="hrp-sr">{over ? `${Math.abs(r.daysLeft)} days overdue` : `in ${r.daysLeft} days`}</span>
              </div>
            </li>
          );
        })}
        {unsigned.map((p) => (
          <li key={p.id} className="hrp-soon__it" data-tone="info">
            <div className="hrp-soon__dd" aria-hidden="true"><b><FiEdit3 /></b><span>policy</span></div>
            <div>
              <div className="hrp-soon__tt">{`${p.title} v${p.version}`}</div>
              <div className="hrp-soon__ts">Not signed yet</div>
            </div>
          </li>
        ))}
      </ul>
      {(open.length > 0 || unsigned.length > 0) && (
        <div className="hrp-soon__more">
          <button type="button" className="hrp-link" onClick={() => onTab('reminders')}>Reminders <FiArrowRight aria-hidden="true" /></button>
          <button type="button" className="hrp-link" onClick={() => onTab('policies')}>Policies <FiArrowRight aria-hidden="true" /></button>
        </div>
      )}
    </Card>
  );
}

export function ChangeLogList({ rows, typesById }) {
  return (
    <ul className="hrp-log">
      {rows.map((row) => (
        <li key={row.id} className="hrp-log__it">
          <i aria-hidden="true" />
          <div>
            <span><b>{actorLabel(row)}</b>{` ${describeChange(row, typesById)}`}</span>
            <em>{stampLabel(row.at)}</em>
          </div>
        </li>
      ))}
    </ul>
  );
}

function ChangeLogCard({ rows, typesById, onTab }) {
  const latest = (rows || []).slice(0, 4);
  return (
    <Card
      delay={3}
      as="section"
      aria-label="Latest changes"
      title="Change log"
      className="hrp-logcard"
      action={latest.length > 0 && <button type="button" className="hrp-link" onClick={() => onTab('changelog')}>All <FiArrowRight aria-hidden="true" /></button>}
    >
      {latest.length === 0 ? <p className="hrp-soon__none">No changes yet.</p> : <ChangeLogList rows={latest} typesById={typesById} />}
    </Card>
  );
}

function SickRunBanner({ runs }) {
  if (!runs || runs.length === 0) return null;
  const list = runs.map((r) => `${shortRangeLabel(r.startDate, r.endDate)} (${r.workdays} workdays)`).join(', ');
  return (
    <div className="hrp-banner hrp-pf-run" role="note">
      <span className="hrp-banner__ic" aria-hidden="true"><FiAlertTriangle /></span>
      <div className="hrp-banner__txt">
        <div className="hrp-banner__title">3+ sick days in a row</div>
        <div className="hrp-banner__text">{`${list}. Mark documentation requested or received on the entry below.`}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------------------------

export function ProfileTimeOff({ data, types, holidays, today, onLog, onEdit, onRemoved, onPatchEntry, onBalancesSaved, onTab }) {
  const typesById = useMemo(() => new Map((types || []).map((t) => [t.id, t])), [types]);
  const holidaySet = useMemo(() => new Set((holidays || []).map((h) => h.date)), [holidays]);
  const entries = data.entries || [];
  return (
    <>
      <SickRunBanner runs={data.sickRuns} />
      <div className="hrp-pf-cols">
        <div className="hrp-stack">
          <BalancesCard personId={data.person.id} year={data.year} balances={data.balances || []} onSaved={onBalancesSaved} />
          <EntriesCard
            entries={entries.filter((e) => !e.protected)}
            sickRuns={data.sickRuns}
            today={today}
            year={data.year}
            holidays={holidaySet}
            onLog={onLog}
            onEdit={onEdit}
            onRemoved={onRemoved}
            onPatched={onPatchEntry}
          />
          <ProtectedCard
            entries={entries.filter((e) => e.protected)}
            totals={data.protectedTotals || []}
            today={today}
            holidays={holidaySet}
            onEdit={onEdit}
            onRemoved={onRemoved}
            onPatched={onPatchEntry}
          />
        </div>
        <div className="hrp-stack hrp-pf-side">
          <ComingUp reminders={data.reminders} policies={data.policies} onTab={onTab} />
          <ChangeLogCard rows={data.changelog} typesById={typesById} onTab={onTab} />
        </div>
      </div>
    </>
  );
}

export default ProfileTimeOff;
